import { apiFetch, apiStream } from '@/services/api-interface'
import logger from '@/services/logger/logger'
import { AnyError, ApiError, ReduxAction, StreamError } from '../interfaces'
import {
  addNotificationsAction,
  ADD_NOTIFICATIONS,
  ERROR_FETCHING_NOTIFICATIONS,
  FETCHING_NOTIFICATIONS,
  loadMoreNotificationsAction,
  LOAD_MORE_NOTIFICATIONS,
  markNotificationStatusAction,
  MARK_NOTIFICATION_STATUS,
  removeNotificationsAction,
  REMOVE_NOTIFICATIONS,
  setErrorFetchingNotifications,
  setFetchingNotifications,
  setNotificationsAttributes,
  setNotificationsCount,
  SET_NOTIFICATIONS_COUNT,
  SET_NOTIFICATIONS_ATTRIBUTES,
} from './actions'
import { NotificationsAttrKeys, NotificationsKeys, NotificationsState, ReachMeNotification } from './interfaces'
import store, { currentState } from '@/store'
import axios, { AxiosError } from 'axios'
import { manageStream, pushAnalyticsEvent } from '@/services/utils'
import { EventDetail } from '@/config/interface'

export default function NotificationsReducer(
  state: NotificationsState = {} as NotificationsState,
  action: ReduxAction,
): NotificationsState {
  switch (action.type) {
    case SET_NOTIFICATIONS_COUNT: {
      return {
        ...state,
        ...action.payload,
      }
    }
    case ADD_NOTIFICATIONS:
      return {
        ...state,
        list: appendNotifications(state.list, action.payload),
        hasLoaded: true,
      }
    case LOAD_MORE_NOTIFICATIONS:
      return {
        ...state,
        list: appendNotifications(action.payload, state.list),
        allNotificationsLoaded: action.payload.length === 0,
      }
    case REMOVE_NOTIFICATIONS:
      return {
        ...state,
        list: state.list.filter((notification) => !(action.payload as string[]).includes(notification.key)),
      }
    case MARK_NOTIFICATION_STATUS:
      return {
        ...state,
        list: markNotificationStatus(state.list, action.payload),
      }
    case FETCHING_NOTIFICATIONS:
      return {
        ...state,
        isFetching: action.payload,
      }
    case ERROR_FETCHING_NOTIFICATIONS:
      return {
        ...state,
        error: formatApiError(action.payload),
      }
    case SET_NOTIFICATIONS_ATTRIBUTES: {
      const newState = state
      newState.attrs[action.payload.key] = action.payload.value
      return newState
    }
    default:
      return state
  }
}

//Stream
export const getNotificationsCount = (): void => {
  const name = 'notificationsCount'
  apiStream({
    name,
    url: '/notifications/streams/count',
  })
    .then((response) => {
      manageStream(name, response, getNotificationsCount)
      const countEventListener = (e: Event) => {
        // cast event to correct type
        const event = e as MessageEvent
        const count: NotificationsState = JSON.parse(event.data)
        store.dispatch(setNotificationsCount(count))
      }
      response.addEventListener('count', countEventListener)
    })
    .catch((err: Error) => {
      logger.error(err)
    })
}

const manageFetch = (numberOfNotifications: number): void =>
  numberOfNotifications ? fetchNotifications(0, numberOfNotifications) : fetchNotifications()

const sendAnalytics = (label: string): void => {
  const event: EventDetail = {
    category: 'action',
    action: 'header.notifications_&_messages',
    label,
  }
  pushAnalyticsEvent(event)
}

export const getNotificationsStream = (): void => {
  const name = 'notifications'
  apiStream({
    name,
    url: '/notifications/streams/data',
  })
    .then((stream) => {
      manageStream(name, stream, getNotificationsStream)
      manageFetch(currentState.notifications?.list.length)
      const notificationCreatedListener = (e: Event) => {
        // cast event to correct type
        const event = e as MessageEvent
        const newNotification: ReachMeNotification = JSON.parse(event.data)
        store.dispatch(addNotificationsAction([newNotification]))
      }
      const notificationDeletedListener = (e: Event) => {
        const event = e as MessageEvent
        const notificationToDelete: string = JSON.parse(event.data).key
        store.dispatch(removeNotificationsAction([notificationToDelete]))
        sendAnalytics('dismiss')
      }
      const notificationReadListener = (e: Event) => {
        const event = e as MessageEvent
        const { key, read } = JSON.parse(event.data)
        store.dispatch(markNotificationStatusAction({ key, value: read }))
      }

      const errorListener = (e: Event) => {
        logger.error(e)
        // cast to error
        formatApiError(e as unknown as StreamError)
      }

      stream.addEventListener('notification_created', notificationCreatedListener)
      stream.addEventListener('notification_deleted', notificationDeletedListener)
      stream.addEventListener('notification_read', notificationReadListener)
      stream.addEventListener('error', errorListener)
    })
    .catch((err: Error) => {
      logger.error(err)
      formatApiError(err)
    })
}

// Thunks
export const fetchNotifications = (skip = 0, limit = 20): void => {
  store.dispatch(setFetchingNotifications(true))
  apiFetch({
    url: '/notifications',
    method: 'GET',
    params: {
      limit,
      skip,
    },
  })
    .then((response) => {
      if (skip === 0) {
        store.dispatch(addNotificationsAction(response.data))
      } else {
        store.dispatch(loadMoreNotificationsAction(response.data))
      }
      store.dispatch(setErrorFetchingNotifications(null))
    })
    .catch((err: Error | AxiosError) => {
      store.dispatch(setErrorFetchingNotifications(err))
    })
    .finally(() => {
      store.dispatch(setFetchingNotifications(false))
    })
}

const appendNotifications = (initialNotifications: ReachMeNotification[], newNotifications: ReachMeNotification[]) => {
  // TODO if notification version is greater, override it
  // add keys to Set to improve performance when filtering duplicates
  const initialKeys = new Set(initialNotifications.map((notification) => notification.key))
  return [...newNotifications.filter((notification) => !initialKeys.has(notification.key)), ...initialNotifications]
}

const markNotificationStatus = (notifications: ReachMeNotification[], status: NotificationsAttrKeys) => {
  const newNotifications = [...notifications]
  const oldNotification = newNotifications.find((notification) => notification.key === status.key)
  if (oldNotification) {
    oldNotification.read = status.value
    sendAnalytics(status.value ? 'read' : 'unread')
  }
  return newNotifications
}

export const setNotificationsStatus = (notificationKeys: NotificationsKeys, read: boolean): void => {
  apiFetch({
    url: '/notifications/read',
    method: 'PATCH',
    data: { ...notificationKeys, read },
  }).catch((err: Error | AxiosError) => {
    if (axios.isAxiosError(err)) {
      console.error(err)
    }
    console.error(err)
  })
}

export const deleteNotification = (notificatioKey: string): void => {
  apiFetch({
    url: `/notifications/${notificatioKey}`,
    method: 'DELETE',
  }).catch((err: Error | AxiosError) => {
    if (axios.isAxiosError(err)) {
      console.error(err)
    }
    console.error(err)
  })
}

export const getFirstUsageBanner = (): void => {
  apiFetch({
    url: '/notifications/customers/attributes',
    method: 'GET',
  })
    .then((resp) => {
      Object.keys(resp.data).forEach((el: string) =>
        store.dispatch(setNotificationsAttributes({ key: el, value: resp.data[el] })),
      )
    })
    .catch((err: Error | AxiosError) => {
      console.log(err)
    })
}

export const setFirstUsageBanner = (attr: Record<string, boolean>): void => {
  apiFetch({
    url: '/notifications/customers/attributes',
    method: 'PATCH',
    data: attr,
  })
    .then(() =>
      store.dispatch(setNotificationsAttributes({ key: Object.keys(attr)[0], value: Object.values(attr)[0] })),
    )
    .catch((err: Error | AxiosError) => {
      console.log(err)
    })
}

/* TODO: Move this somewhere else? */
const formatApiError = (err: AnyError | null) => {
  if (!err) {
    return err
  }

  let formattedError: ApiError
  if (axios.isAxiosError(err)) {
    const { response } = err as AxiosError
    formattedError = {
      status: response?.status,
      message: err.message,
      data: response?.data,
    }
  } else if ('status' in err) {
    formattedError = {
      status: (err as StreamError).status,
      message: err.message,
    }
  } else {
    formattedError = {
      message: err.message,
    }
  }
  return formattedError
}
